import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stream } from '@/types';
import { api } from '@/services/api';
import LivepeerPlayer from '@/components/video/LivepeerPlayer';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const getUserStreams = async (): Promise<Stream[]> => {
  const response = await api.get('/api/streams/me');
  if (response && response.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  return [];
};

const MyStreamsPage: React.FC = () => {
  const { data: streams = [], isLoading, error } = useQuery<Stream[]>({
    queryKey: ['myStreams'],
    queryFn: getUserStreams,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-dark-800 rounded-lg p-4 h-48"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-500">
          Error loading streams. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">My Streams</h1>
        <Link
          to="/dashboard/streams/create"
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus />
          <span>Create New Stream</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {streams.map((stream: Stream) => (
          <div key={stream.id} className="card">
            <div className="aspect-video mb-4">
              <LivepeerPlayer
                playbackId={stream.playbackId}
                title={stream.title}
                poster={stream.thumbnailUrl}
                autoPlay={false}
                muted={true}
              />
            </div>
            <div className="p-4">
              <h2 className="text-xl font-semibold text-white mb-2">{stream.title}</h2>
              <p className="text-gray-400 mb-4 line-clamp-2">{stream.description}</p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  Created: {new Date(stream.createdAt).toLocaleDateString()}
                </span>
                <div className="flex space-x-2">
                  <Link
                    to={`/dashboard/streams/${stream.id}/edit`}
                    className="btn-icon btn-outline"
                    title="Edit Stream"
                  >
                    <FiEdit2 />
                  </Link>
                  <button
                    className="btn-icon btn-outline text-red-500 hover:text-red-600"
                    title="Delete Stream"
                    onClick={() => {
                      // TODO: Implement delete functionality
                    }}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {streams.length === 0 && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 mb-4">You haven't created any streams yet</div>
            <Link to="/dashboard/streams/create" className="btn-primary">
              Create Your First Stream
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyStreamsPage;
